import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FlaskConical, Loader2, ChevronDown, ChevronRight,
  AlertTriangle, CheckCircle2, ExternalLink,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  getEncounterLabOrders, getAppointmentLabOrders,
  getOrderStatusLabel, getOrderStatusColor,
  LabOrder, LabResult,
} from '@/services/labService';
import { cacheKeys } from '@/lib/cacheKeys';
import LabOrderDetailSheet from '@/components/labs/LabOrderDetailSheet';

interface LinkedLabOrdersListProps {
  clinicId: string;
  /**
   * Encounter scope. Pass when rendered inside the cockpit / for an
   * already-opened visit. The dedicated encounter endpoint is used.
   */
  encounterId?: string | null;
  /**
   * Appointment scope. Pass when rendered pre-visit (AppointmentSheet)
   * before any encounter exists. Falls back to the appointment-scoped
   * endpoint, which surfaces orders placed with `pre_visit_lab` reason.
   *
   * If both are provided, encounterId wins.
   */
  appointmentId?: string | null;
  /** Empty-state message shown when no matching orders exist. */
  emptyMessage?: string;
}

const isAbnormal = (i?: string) =>
  ['high', 'low', 'critical', 'abnormal', 'H', 'L', 'C'].includes(i || '');
const isCritical = (i?: string) =>
  ['critical', 'C'].includes(i || '');

/**
 * Computes a one-line summary from a LabOrder's loaded results array.
 * Returns null when there are no LOINC-coded results yet (order not
 * resulted, or only the legacy summary row exists).
 */
const summarizeResults = (results?: LabResult[]) => {
  if (!results?.length) return null;
  // The backend filters to loinc_code IS NOT NULL, but be defensive.
  const standardized = results.filter(r => r.loinc_code);
  if (!standardized.length) return null;

  const total = standardized.length;
  const abnormal = standardized.filter(r => isAbnormal(r.interpretation)).length;
  const critical = standardized.filter(r => isCritical(r.interpretation)).length;

  return { total, abnormal, critical };
};

const LinkedLabOrdersList: React.FC<LinkedLabOrdersListProps> = ({
  clinicId, encounterId, appointmentId, emptyMessage,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);

  // Encounter scope wins when both are provided — once the visit is
  // open, encounter is the canonical anchor.
  const scope: 'encounter' | 'appointment' | null = encounterId
    ? 'encounter'
    : appointmentId
      ? 'appointment'
      : null;

  const { data: orders = [], isLoading } = useQuery({
    queryKey: scope === 'encounter'
      ? cacheKeys.encounterLabOrders(encounterId!)
      : cacheKeys.appointmentLabOrders(appointmentId!),
    queryFn: () => scope === 'encounter'
      ? getEncounterLabOrders(clinicId, encounterId!)
      : getAppointmentLabOrders(clinicId, appointmentId!),
    enabled: !!clinicId && !!scope,
  });

  const renderResultsRow = (order: LabOrder) => {
    const summary = summarizeResults(order.results);
    if (!summary) {
      return (
        <span className='text-xs text-slate-500'>Awaiting results</span>
      );
    }
    return (
      <div className='flex items-center gap-2 text-xs'>
        {summary.critical > 0 ? (
          <span className='flex items-center gap-1 text-rose-700'>
            <AlertTriangle className='h-3.5 w-3.5' />
            {summary.critical} critical
          </span>
        ) : summary.abnormal > 0 ? (
          <span className='flex items-center gap-1 text-amber-700'>
            <AlertTriangle className='h-3.5 w-3.5' />
            {summary.abnormal} abnormal
          </span>
        ) : (
          <span className='flex items-center gap-1 text-emerald-700'>
            <CheckCircle2 className='h-3.5 w-3.5' />
            All normal
          </span>
        )}
        <span className='text-slate-400'>·</span>
        <span className='text-slate-500'>{summary.total} value{summary.total === 1 ? '' : 's'}</span>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className='text-base flex items-center gap-2'>
            <FlaskConical className='h-4 w-4 text-purple-600' />
            Lab orders
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='flex justify-center py-4'>
              <Loader2 className='h-5 w-5 animate-spin text-slate-400' />
            </div>
          ) : orders.length === 0 ? (
            <p className='text-sm text-slate-500'>
              {emptyMessage || 'No lab orders linked to this visit yet.'}
            </p>
          ) : (
            <ul className='divide-y divide-slate-100'>
              {orders.map(o => {
                const isExpanded = expandedId === o.id;
                const standardized = (o.results || []).filter(r => r.loinc_code);
                return (
                  <li key={o.id} className='py-3'>
                    <div className='flex items-start justify-between gap-3'>
                      <button
                        type='button'
                        className='flex-1 text-left'
                        onClick={() => setExpandedId(isExpanded ? null : o.id)}
                      >
                        <div className='flex items-center gap-2'>
                          {standardized.length > 0 ? (
                            isExpanded ? (
                              <ChevronDown className='h-4 w-4 text-slate-400' />
                            ) : (
                              <ChevronRight className='h-4 w-4 text-slate-400' />
                            )
                          ) : <span className='inline-block w-4' />}
                          <span className='text-sm font-medium text-slate-900'>
                            {o.panel_name || o.panel?.name || 'Lab panel'}
                          </span>
                        </div>
                        <div className='ml-6 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500'>
                          <span>
                            Ordered {o.created_at
                              ? new Date(o.created_at).toLocaleDateString('en-US')
                              : '—'}
                          </span>
                          {o.collection_method && (
                            <>
                              <span className='text-slate-300'>·</span>
                              <span>{o.collection_method.replace(/_/g, ' ')}</span>
                            </>
                          )}
                        </div>
                        <div className='ml-6 mt-1'>{renderResultsRow(o)}</div>
                      </button>
                      <div className='flex flex-col items-end gap-1.5'>
                        <Badge className={getOrderStatusColor(o.status)}>
                          {getOrderStatusLabel(o.status)}
                        </Badge>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          className='h-7 gap-1 text-xs'
                          onClick={() => setDetailOrderId(o.id)}
                        >
                          Details <ExternalLink className='h-3 w-3' />
                        </Button>
                      </div>
                    </div>

                    {isExpanded && standardized.length > 0 && (
                      <ul className='ml-6 mt-2 space-y-1 rounded-md bg-slate-50 p-2 text-xs'>
                        {standardized.slice(0, 8).map(r => {
                          const abnormal = isAbnormal(r.interpretation);
                          const critical = isCritical(r.interpretation);
                          return (
                            <li
                              key={r.id}
                              className='flex items-center justify-between gap-2'
                            >
                              <span className='truncate text-slate-700'>
                                {r.test_name || r.loinc_display || r.loinc_code}
                              </span>
                              <span
                                className={
                                  critical
                                    ? 'font-semibold text-rose-700'
                                    : abnormal
                                      ? 'font-semibold text-amber-700'
                                      : 'text-slate-700'
                                }
                              >
                                {r.value != null ? String(r.value) : '—'}
                                {r.unit ? ` ${r.unit}` : ''}
                              </span>
                            </li>
                          );
                        })}
                        {standardized.length > 8 && (
                          <li className='pt-1 text-center text-slate-500'>
                            +{standardized.length - 8} more — open Details to view all
                          </li>
                        )}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {detailOrderId && (
        <LabOrderDetailSheet
          orderId={detailOrderId}
          open={!!detailOrderId}
          onOpenChange={(open) => { if (!open) setDetailOrderId(null); }}
        />
      )}
    </>
  );
};

export default LinkedLabOrdersList;
